<template>
  <div class="users-container">
    <!-- Header with Add Button -->
    <div class="d-flex align-center mb-4">
      <h2 class="text-h5">المستخدمين</h2>
      <v-spacer></v-spacer>
      <v-btn
        v-if="hasPermission('admin.users.store')"
        v-permission="PERMISSIONS.USERS_CREATE"
        color="primary"
        prepend-icon="mdi-plus"
        @click="openAddDialog"
      >
        إضافة مستخدم جديد
      </v-btn>
    </div>
    
    <!-- Search and Filter -->
    <v-card class="mb-4" flat>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="بحث"
              hide-details
              density="compact"
              variant="outlined"
              :clearable="true"
              clear-icon="mdi-close"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="departmentFilter"
              :items="departments"
              label="القسم"
              item-title="name"
              item-value="id"
              hide-details
              density="compact"
              variant="outlined"
              :clearable="true"
              clear-icon="mdi-close"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="roleFilter"
              :items="roles"
              label="الدور"
              item-title="name"
              item-value="id"
              hide-details
              density="compact"
              variant="outlined"
              :clearable="true"
              clear-icon="mdi-close"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="statusFilter"
              :items="[
                { title: 'الكل', value: 'all' },
                { title: 'نشط', value: 'active' },
                { title: 'غير نشط', value: 'inactive' }
              ]"
              label="الحالة"
              item-title="title"
              item-value="value"
              hide-details
              density="compact"
              variant="outlined"
            
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    
    <!-- Users Table -->
    <v-card>
      <v-data-table
        :headers="headers"
        :items="filteredUsers"
        :loading="loading"
        color="primary"
        first-icon="mdi-chevron-left"
        last-icon="mdi-chevron-right"
        next-icon="mdi-chevron-right"
        prev-icon="mdi-chevron-left"
        show-current-page
        show-first-last-page
        show-first-page
        show-last-page
        show-next-page
        show-previous-page
        class="elevation-1"
      >
        <template #[`item.departments`]="{ item }">
          <div class="d-flex flex-wrap ga-1">
            <v-chip
              v-for="userDept in item.departments"
              :key="userDept.id"
              :color="userDept.active ? 'success' : 'error'"
              variant="outlined"
              size="small"
              class="ma-1"
            >
              <v-icon start size="small">mdi-office-building</v-icon>
              {{ getDepartmentName(userDept.department_id) }}
              <v-icon 
                v-if="!userDept.active" 
                end 
                size="small" 
                color="error"
                title="غير نشط في هذا القسم"
              >
                mdi-pause-circle
              </v-icon>
            </v-chip>
            <span v-if="!item.departments || item.departments.length === 0" class="text-grey">
              لا يوجد أقسام
            </span>
          </div>
        </template>
        
        <template #[`item.roles`]="{ item }">
          <v-chip
            v-for="role in item.roles"
            :key="role.id"
            color="primary"
            variant="outlined"
            size="small"
            class="ma-1"
          >
            {{ role.name }}
          </v-chip>
          <span v-if="!item.roles || item.roles.length === 0" class="text-grey">
            لا يوجد أدوار
          </span>
        </template>
        
        <template #[`item.active`]="{ item }">
          <v-chip
            :color="item.active ? 'success' : 'error'"
            text-color="white"
            size="small"
          >
            {{ item.active ? 'نشط' : 'غير نشط' }}
          </v-chip>
        </template>
        
        <template #[`item.actions`]="{ item }">
          <v-tooltip text="تعديل المستخدم">
            <template v-slot:activator="{ props }">
              <v-btn
              v-if="hasPermission('admin.users.update')"
                v-bind="props"
                v-permission="PERMISSIONS.USERS_EDIT"
                icon
                variant="text"
                size="small"
                color="primary"
                @click="editUser(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
          
          <v-tooltip text="إضافة إلى قسم آخر">
            <template v-slot:activator="{ props }">
              <v-btn
                v-if="hasPermission('admin.users-departments.store')"
                v-bind="props"
                v-permission="PERMISSIONS.USERS_EDIT"
                icon
                variant="text"
                size="small"
                color="success"
                @click="openAddToDepartmentDialog(item)"
              >
                <v-icon>mdi-account-plus</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
          
          <v-tooltip text="إدارة الأقسام">
            <template v-slot:activator="{ props }">
              <v-btn
                v-if="hasPermission('admin.users-departments.update')"
                v-bind="props"
                v-permission="PERMISSIONS.USERS_EDIT"
                icon
                variant="text"
                size="small"
                color="info"
                @click="openManageDepartmentsDialog(item)"
              >
                <v-icon>mdi-office-building-cog</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
    
    <!-- Add/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span>{{ isEditing ? 'تعديل مستخدم' : 'إضافة مستخدم جديد' }}</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <!-- Auth Section -->
            <v-card class="mb-4" variant="outlined">
              <v-card-title class="text-subtitle-1">معلومات الحساب</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="editedItem.emp_no"
                      label="الرقم الوظيفي"
                      :rules="userRules.emp_no"
                      required
                    ></v-text-field>
                  </v-col>
                  
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="editedItem.username"
                      label="اسم المستخدم"
                      :rules="userRules.username"
                      required
                    ></v-text-field>
                  </v-col>
                  
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="editedItem.password"
                      label="كلمة المرور"
                      :rules="[validationRules.password]"
                      :required="!isEditing"
                      type="password"
                    ></v-text-field>
                  </v-col>
                  

                  
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="editedItem.active"
                      label="نشط"
                      color="success"
                      inset
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
            
            <!-- Employee Info Section -->
            <v-card class="mb-4" variant="outlined">
              <v-card-title class="text-subtitle-1">المعلومات الشخصية</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="editedItem.name"
                      label="الاسم"
                      :rules="userRules.name"
                      required
                    ></v-text-field>
                  </v-col>
                  
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="editedItem.email"
                      label="البريد الإلكتروني"
                      :rules="userRules.email"
                      required
                    ></v-text-field>
                  </v-col>
                  
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="editedItem.phone"
                      label="رقم الهاتف"
                      :rules="userRules.phone"
                      required
                    ></v-text-field>
                  </v-col>
                  
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="editedItem.nid"
                      label="الرقم الوطني"
                      :rules="userRules.nid"
                      required
                    ></v-text-field>
                  </v-col>
                  
                  <v-col cols="12">
                    <v-textarea
                      v-model="editedItem.address"
                      label="العنوان"
                      rows="2"
                      :rules="[v => !!v || 'العنوان مطلوب']"
                      required
                    ></v-textarea>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Employee Info Section -->
            <v-card class="mb-4" variant="outlined">
              <v-card-title class="text-subtitle-1">معلومات إضافية</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="editedItem.employee_title_id"
                      :items="employeeTitles"
                      label="المسمى الوظيفي"
                      item-title="name"
                      item-value="id"
                      :rules="[validationRules.required('المسمى الوظيفي')]"
                      required
                    ></v-select>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Signature Upload Section -->
            <v-card variant="outlined">
              <v-card-title class="text-subtitle-1">ختم التوقيع</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12">
                    <v-file-input
                      v-model="stampFile"
                      label="  التوقيع (PNG)"
                     
                      prepend-icon="mdi-camera"
                      show-size
                     
                    ></v-file-input>
                    <div class="text-caption text-grey">
                      يُفضل رفع صورة PNG بخلفية شفافة لأفضل النتائج
                    </div>
                  </v-col>
                  
                  <!-- Stamp Preview -->
                  <v-col cols="12" v-if="stampPreview">
                    <div class="d-flex align-center">
                      <span class="text-subtitle-2 me-4">معاينة الختم:</span>
                      <v-avatar size="80" rounded="lg">
                        <v-img :src="stampPreview" alt="Stamp Preview" cover></v-img>
                      </v-avatar>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="save"
          >
            حفظ
          </v-btn>
          <v-btn
            color="error"
            variant="text"
            @click="close"
          >
            إلغاء
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add to Department Dialog -->
    <v-dialog v-model="addToDepartmentDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span>إضافة المستخدم إلى قسم جديد</span>
        </v-card-title>
        
        <v-card-subtitle v-if="selectedUserForDepartment">
          <div class="d-flex align-center">
            <v-icon class="me-2" color="primary">mdi-account</v-icon>
            <span><strong>المستخدم:</strong> {{ selectedUserForDepartment.name }}</span>
          </div>
        </v-card-subtitle>
        
        <v-card-text>
          <v-form ref="departmentForm" v-model="departmentFormValid">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="newDepartmentData.department_id"
                  :items="availableDepartmentsForAdd"
                  label="القسم"
                  item-title="name"
                  item-value="id"
                  :rules="[validationRules.required('القسم')]"
                  required
                  @update:model-value="onNewDepartmentChange"
                >
                  <template v-slot:item="{ props }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend>
                        <v-icon>mdi-office-building</v-icon>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12">
                <v-select
                  v-model="newDepartmentData.department_job_id"
                  :items="filteredNewDepartmentJobs"
                  label="وظيفة القسم"
                  item-title="name"
                  item-value="id"
                  :rules="[validationRules.required('وظيفة القسم')]"
                  required
                  
                  placeholder="اختر القسم أولاً"
                >
                  <template v-slot:item="{ props }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend>
                        <v-icon>mdi-briefcase</v-icon>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12">
                <v-select
                  v-model="newDepartmentData.roles_ids"
                  :items="roles"
                  label="الأدوار"
                  item-title="name"
                  item-value="id"
                  :rules="[validationRules.required('الأدوار')]"
                  required
                  multiple
                  chips
                  closable-chips
                >
                  <template v-slot:item="{ props }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend>
                        <v-icon>mdi-shield-account</v-icon>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12">
                <v-switch
                  v-model="newDepartmentData.active"
                  label="نشط في القسم"
                  color="success"
                  inset
                ></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!departmentFormValid"
            :loading="loading"
            @click="addUserToDepartment"
          >
            إضافة إلى القسم
          </v-btn>
          <v-btn
            color="error"
            variant="text"
            @click="closeAddToDepartmentDialog"
          >
            إلغاء
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Manage Departments Dialog -->
    <v-dialog v-model="manageDepartmentsDialog" max-width="900px">
      <v-card>
        <v-card-title>
          <span>إدارة أقسام المستخدم</span>
        </v-card-title>
        
        <v-card-subtitle v-if="selectedUserForManagement">
          <div class="d-flex align-center">
            <v-icon class="me-2" color="primary">mdi-account</v-icon>
            <span><strong>المستخدم:</strong> {{ selectedUserForManagement.name }}</span>
          </div>
        </v-card-subtitle>
        
        <v-card-text>
          <div class="mb-4">
            <h3 class="text-h6 mb-3">الأقسام الحالية</h3>
            <v-row>
              <v-col 
                v-for="userDept in userDepartments" 
                :key="userDept.id"
                cols="12" 
                md="6"
              >
                <v-card variant="outlined" class="mb-2">
                  <v-card-text class="pb-2">
                    <div class="d-flex justify-space-between align-center">
                      <div>
                        <div class="d-flex align-center mb-1">
                          <v-icon class="me-2" color="primary">mdi-office-building</v-icon>
                          <strong>{{ getDepartmentName(userDept.department_id) }}</strong>
                          <v-chip 
                            :color="userDept.active ? 'success' : 'error'" 
                            size="x-small" 
                            class="ml-2"
                          >
                            {{ userDept.active ? 'نشط' : 'غير نشط' }}
                          </v-chip>
                        </div>
                        <div class="text-caption text-grey">
                          <strong>الوظيفة:</strong> {{ getDepartmentJobName(userDept.department_job_id) }}
                        </div>
                        <div class="text-caption text-grey">
                          <strong>الأدوار:</strong> 
                          <v-chip
                            v-for="role in userDept.roles"
                            :key="role.id"
                            size="x-small"
                            color="primary"
                            variant="outlined"
                            class="ma-1"
                          >
                            {{ role.name }}
                          </v-chip>
                        </div>
                      </div>
                      <div class="d-flex flex-column ga-1">
                        <v-tooltip text="تعديل القسم">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-if="hasPermission('admin.users-departments.update')"
                              v-bind="props"
                              icon
                              variant="text"
                              size="small"
                              color="primary"
                              @click="openEditDepartmentDialog(userDept)"
                            >
                              <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                        </v-tooltip>
                        
                        <v-tooltip text="حذف من القسم">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-if="hasPermission('admin.users-departments.destroy')"
                              v-bind="props"
                              icon
                              variant="text"
                              size="small"
                              color="error"
                              @click="confirmRemoveFromDepartment(userDept)"
                            >
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                        </v-tooltip>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col v-if="userDepartments.length === 0" cols="12">
                <v-alert type="info" variant="tonal">
                  <v-icon>mdi-information</v-icon>
                  هذا المستخدم غير مُسجل في أي قسم
                </v-alert>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-btn
            color="success"
            prepend-icon="mdi-plus"
            @click="openAddToDepartmentFromManage"
          >
            إضافة إلى قسم جديد
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="closeMangeDepartmentsDialog"
          >
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Department Dialog -->
    <v-dialog v-model="editDepartmentDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span>تعديل بيانات القسم</span>
        </v-card-title>
        
        <v-card-subtitle v-if="selectedUserForManagement">
          <div class="d-flex align-center">
            <v-icon class="me-2" color="primary">mdi-account</v-icon>
            <span><strong>المستخدم:</strong> {{ selectedUserForManagement.name }}</span>
          </div>
          <div class="d-flex align-center mt-1">
            <v-icon class="me-2" color="info">mdi-office-building</v-icon>
            <span><strong>القسم:</strong> {{ getDepartmentName(editingDepartmentData.department_id) }}</span>
          </div>
        </v-card-subtitle>
        
        <v-card-text>
          <v-form ref="editDepartmentForm" v-model="editDepartmentFormValid">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="editingDepartmentData.department_job_id"
                  :items="filteredEditDepartmentJobs"
                  label="وظيفة القسم"
                  item-title="name"
                  item-value="id"
                  :rules="[validationRules.required('وظيفة القسم')]"
                  required
                >
                  <template v-slot:item="{ props }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend>
                        <v-icon>mdi-briefcase</v-icon>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12">
                <v-select
                  v-model="editingDepartmentData.roles_ids"
                  :items="roles"
                  label="الأدوار"
                  item-title="name"
                  item-value="id"
                  :rules="[validationRules.required('الأدوار')]"
                  required
                  multiple
                  chips
                  closable-chips
                >
                  <template v-slot:item="{ props }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend>
                        <v-icon>mdi-shield-account</v-icon>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12">
                <v-switch
                  v-model="editingDepartmentData.active"
                  label="نشط في القسم"
                  color="success"
                  inset
                ></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!editDepartmentFormValid"
            :loading="loading"
            @click="updateUserDepartment"
          >
            حفظ التعديلات
          </v-btn>
          <v-btn
            color="error"
            variant="text"
            @click="closeEditDepartmentDialog"
          >
            إلغاء
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove from Department Confirmation Dialog -->
    <v-dialog v-model="removeFromDepartmentDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon color="warning" class="me-2">mdi-alert</v-icon>
          تأكيد الحذف من القسم
        </v-card-title>
        
        <v-card-text>
          <div class="text-body-1 mb-3">
            هل أنت متأكد من إزالة المستخدم 
            <strong>"{{ selectedUserForManagement?.name }}"</strong> 
            من قسم 
            <strong>"{{ getDepartmentName(departmentToRemove?.department_id) }}"</strong>؟
          </div>
          
          <v-alert type="warning" variant="tonal" density="compact">
            <v-icon>mdi-information</v-icon>
            سيفقد المستخدم جميع الصلاحيات المرتبطة بهذا القسم
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            :loading="loading"
            @click="executeRemoveFromDepartment"
          >
            نعم، احذف من القسم
          </v-btn>
          <v-btn
            color="primary"
            variant="text"
            @click="removeFromDepartmentDialog = false"
          >
            إلغاء
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import userService from '@/services/userService'
import departmentService from '@/services/departmentService'
import departmentJobService from '@/services/departmentJobService'
import employeeTitleService from '@/services/employeeTitleService'
import roleService from '@/services/roleService'
import { validationRules, formValidation } from '@/assets/validations'
import { PERMISSIONS } from '@/utils/permissions'
import { hasPermission, hasModule } from '@/utils/permissions'
import { showSuccessAlert, showErrorAlert } from '@/utils/alertUtils'

export default {
  name: 'UsersView',
  data() {
    return {
      loading: false,
      dialog: false,
      isEditing: false,
      valid: false,
      search: '',
      departmentFilter: null,
      roleFilter: null,
      statusFilter: 'all',
      
      // Pagination
      page: 1,
      perPage: 15,
      totalItems: 0,
      
      headers: [
        { title: 'الرقم الوظيفي', key: 'emp_no', align: 'start' },
        { title: 'الاسم', key: 'name' },
        { title: 'اسم المستخدم', key: 'username' },
        { title: 'البريد الإلكتروني', key: 'email' },
        { title: 'الهاتف', key: 'phone' },
        { title: 'الأقسام', key: 'departments' },
       
        { title: 'الحالة', key: 'active' },
        { title: 'الإجراءات', key: 'actions', sortable: false }
      ],
      departments: [],
      departmentJobs: [],
      filteredDepartmentJobs: [],
      employeeTitles: [],
      roles: [],
      users: [],
      editedItem: {
        id: null,
        name: '',
        username: '',
        email: '',
        phone: '',
        emp_no: '',
        nid: '',
        employee_title_id: null,
        address: '',
        password: '',
        active: true,
        stamp: null
      },
      defaultItem: {
        id: null,
        name: '',
        username: '',
        email: '',
        phone: '',
        emp_no: '',
        nid: '',
        employee_title_id: null,
        address: '',
        password: '',
        active: true,
        stamp: null
      },
      
      // Validation rules using centralized validation
      userRules: formValidation.getUserFormRules(),
      validationRules: validationRules,

      // Stamp related data
      stampFile: null,
      stampPreview: null,
      
      // Add to Department dialog data
      addToDepartmentDialog: false,
      departmentFormValid: false,
      selectedUserForDepartment: null,
      filteredNewDepartmentJobs: [],
      fromManagementDialog: false, // Track if opened from management dialog
      newDepartmentData: {
        department_id: null,
        department_job_id: null,
        active: true,
        roles_ids: []
      },
      defaultNewDepartmentData: {
        department_id: null,
        department_job_id: null,
        active: true,
        roles_ids: []
      },
      
      // Manage Departments dialog data
      manageDepartmentsDialog: false,
      selectedUserForManagement: null,
      userDepartments: [],
      
      // Edit Department dialog data
      editDepartmentDialog: false,
      editDepartmentFormValid: false,
      editingDepartmentData: {
        id: null,
        department_id: null,
        department_job_id: null,
        active: true,
        roles_ids: []
      },
      filteredEditDepartmentJobs: [],
      
      // Remove from Department dialog data
      removeFromDepartmentDialog: false,
      departmentToRemove: null
    }
  },
  
  mounted() {
    this.fetchUsers()
    this.fetchDepartments()
    this.fetchRoles()
    this.fetchDepartmentJobs()
    this.fetchEmployeeTitles()
  },
  
  computed: {
    PERMISSIONS() {
      return PERMISSIONS
    },

    stampRules() {
      // Stamp is only required for new users, optional for editing
      return this.isEditing 
        ? [] 
        : [v => !!v || 'ختم التوقيع مطلوب'];
    },
    
    filteredUsers() {
      let result = [...this.users];
      
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        result = result.filter(u => 
          u.emp_no?.toLowerCase().includes(searchLower) ||
          u.name?.toLowerCase().includes(searchLower) ||
          u.username?.toLowerCase().includes(searchLower) ||
          u.email?.toLowerCase().includes(searchLower)
        );
      }
      
      if (this.departmentFilter) {
        result = result.filter(u => u.department_id === this.departmentFilter);
      }
      
      if (this.roleFilter) {
        result = result.filter(u => u.roles_ids?.includes(this.roleFilter));
      }
      
      if (this.statusFilter !== 'all') {
        const isActive = this.statusFilter === 'active';
        result = result.filter(u => u.active === isActive);
      }
      
      return result;
    },

    // Available departments for adding user (excluding ones they're already in)
    availableDepartmentsForAdd() {
      if (!this.selectedUserForDepartment || !this.selectedUserForDepartment.departments) {
        return this.departments;
      }
      
      const userDepartmentIds = this.selectedUserForDepartment.departments.map(ud => ud.department_id);
      return this.departments.filter(dept => !userDepartmentIds.includes(dept.id));
    }
  },
  
  methods: {

    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },


    async fetchDepartments() {
      try {
        const response = await departmentService.getDepartments()
        // Handle nested paginated response structure
        if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
          this.departments = response.data.data.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.departments = response.data.data;
        } else if (response.data && Array.isArray(response.data)) {
          this.departments = response.data;
        } else {
          this.departments = [];
        }
      } catch (error) {
        console.error('Error fetching departments:', error)
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: 'فشل في جلب الأقسام'
        })
      }
    },

    async fetchRoles() {
      try {
        const response = await roleService.getRoles()
        // Handle nested paginated response structure
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.roles = response.data.data;
        } else if (response.data && Array.isArray(response.data)) {
          this.roles = response.data;
        } else {
          this.roles = [];
        }
      } catch (error) {
        console.error('Error fetching roles:', error)
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: 'فشل في جلب الأدوار'
        })
      }
    },

    async fetchDepartmentJobs() {
      try {
        const response = await departmentJobService.getDepartmentJobs()
        // Handle nested paginated response structure
        if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
          this.departmentJobs = response.data.data.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.departmentJobs = response.data.data;
        } else if (response.data && Array.isArray(response.data)) {
          this.departmentJobs = response.data;
        } else {
          this.departmentJobs = [];
        }
      } catch (error) {
        console.error('Error fetching department jobs:', error)
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: 'فشل في جلب وظائف الأقسام'
        })
      }
    },

    async fetchEmployeeTitles() {
      try {
        const response = await employeeTitleService.getEmployeeTitles()
        // Handle nested paginated response structure
        if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
          this.employeeTitles = response.data.data.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.employeeTitles = response.data.data;
        } else if (response.data && Array.isArray(response.data)) {
          this.employeeTitles = response.data;
        } else {
          this.employeeTitles = [];
        }
      } catch (error) {
        console.error('Error fetching employee titles:', error)
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: 'فشل في جلب المسميات الوظيفية'
        })
      }
    },
    
    async fetchUsers() {
      try {
        this.loading = true
        const response = await userService.getUsers()
        
        // Handle nested paginated response structure
        if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
          this.users = response.data.data.data;
          this.totalItems = response.data.data.total || response.data.data.data.length;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.users = response.data.data;
          this.totalItems = response.data.data.length;
        } else if (response.data && Array.isArray(response.data)) {
          this.users = response.data;
          this.totalItems = response.data.length;
        } else {
          this.users = [];
          this.totalItems = 0;
        }
        
        //console.log('Loaded users:', this.users);
      } catch (error) {
        console.error('Error fetching users:', error)
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: 'فشل في جلب المستخدمين'
        })
      } finally {
        this.loading = false
      }
    },
    
    openAddDialog() {
      this.isEditing = false;
      this.editedItem = { ...this.defaultItem };
      this.dialog = true;
    },
    
    editUser(item) {
      this.isEditing = true;
      this.editedItem = { 
        ...item,
        employee_title_id: item.employee_title_id || null,
        password: '' // Reset password field for editing
      };
      
      // Set stamp preview if exists
      if (item.stamp_url) {
        this.stampPreview = item.stamp_url;
      }
      
      this.dialog = true;
    },
    
    async save() {
      if (!this.$refs.form.validate()) return;
      
      try {
        this.loading = true;
        
        // Prepare user data for FormData
        const userData = {
          name: this.editedItem.name,
          username: this.editedItem.username,
          email: this.editedItem.email,
          phone: this.editedItem.phone,
          emp_no: this.editedItem.emp_no,
          nid: this.editedItem.nid,
          employee_title_id: this.editedItem.employee_title_id,
          address: this.editedItem.address,
          active: this.editedItem.active ? 1 : 0
        };

        // Add password only if provided
        if (this.editedItem.password) {
          userData.password = this.editedItem.password;
        }

        // Add stamp file if provided
        if (this.stampFile) {
          userData.stamp = this.stampFile;
        }

        // Create FormData
        const formData = userService.createFormData(userData);
        
        if (this.isEditing) {
          await userService.updateUser(this.editedItem.id, formData);
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم تحديث المستخدم بنجاح'
          });
        } else {
          await userService.createUser(formData);
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم إضافة المستخدم بنجاح'
          });
        }
        
        await this.fetchUsers(); // Refresh the list
        this.close();
      } catch (error) {
        console.error('Error saving user:', error);
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'حدث خطأ أثناء حفظ بيانات المستخدم'
        });
      } finally {
        this.loading = false;
      }
    },
    
    close() {
      this.dialog = false;
      this.stampFile = null;
      this.stampPreview = null;
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.$refs.form?.reset();
      });
    },
    
    getRoleNames(roles) {
      return roles?.map(role => role.name).join(', ') || 'لا يوجد';
    },

    getDepartmentName(departmentId) {
      if (!departmentId) return 'غير محدد';
      const department = this.departments.find(dept => dept.id === departmentId);
      return department ? department.name : 'غير معروف';
    },

    getDepartmentJobName(departmentJobId) {
      if (!departmentJobId) return 'غير محدد';
      const job = this.departmentJobs.find(job => job.id === departmentJobId);
      return job ? job.name : 'غير معروف';
    },



    async onStampChange(file) {
      if (file && file.length > 0) {
        this.stampFile = file[0];
        const reader = new FileReader();
        reader.onload = (e) => {
          this.stampPreview = e.target.result;
        };
        reader.readAsDataURL(this.stampFile);
      } else {
        this.stampFile = null;
        this.stampPreview = null;
      }
    },

    // Add to Department methods
    openAddToDepartmentDialog(user, fromManagement = false) {
      this.selectedUserForDepartment = user;
      this.newDepartmentData = { ...this.defaultNewDepartmentData };
       this.filteredNewDepartmentJobs = this.departmentJobs;
      this.fromManagementDialog = fromManagement;
      this.addToDepartmentDialog = true;
    },

    closeAddToDepartmentDialog() {
      this.addToDepartmentDialog = false;
      this.selectedUserForDepartment = null;
      this.newDepartmentData = { ...this.defaultNewDepartmentData };
      this.filteredNewDepartmentJobs = [];
      this.fromManagementDialog = false;
      this.$refs.departmentForm?.reset();
    },

    onNewDepartmentChange() {
      
      // Filter department jobs based on selected department
      //console.log('Selected department ID:', this.newDepartmentData.department_id);
      //console.log('All department jobs:', this.departmentJobs);
     
      //console.log('Filtered department jobs:', this.filteredNewDepartmentJobs);
    },

    async addUserToDepartment() {
      if (!this.$refs.departmentForm.validate()) return;
      
      try {
        this.loading = true;
        
        const departmentData = {
          department_id: this.newDepartmentData.department_id,
          department_job_id: this.newDepartmentData.department_job_id,
          active: this.newDepartmentData.active ? 1 : 0,
          roles_ids: this.newDepartmentData.roles_ids
        };

        // Store user reference before the API call
        const currentUser = this.selectedUserForDepartment;
        const wasFromManagement = this.fromManagementDialog;

        const response = await userService.addUserToDepartment(currentUser.id, departmentData);
        
        showSuccessAlert(this.$store, response, `تم إضافة المستخدم "${currentUser.name}" إلى القسم بنجاح`);

        this.closeAddToDepartmentDialog();
        await this.fetchUsers(); // Refresh the users list
        
        // If we came from the management dialog, reopen it with updated data
        if (wasFromManagement) {
          const updatedUser = this.users.find(u => u.id === currentUser.id);
          if (updatedUser) {
            this.openManageDepartmentsDialog(updatedUser);
          }
        }
        
      } catch (error) {
        console.error('Error adding user to department:', error);
        showErrorAlert(this.$store, error, 'فشل في إضافة المستخدم إلى القسم');
      } finally {
        this.loading = false;
      }
    },

    // Manage Departments methods
    openManageDepartmentsDialog(user) {
      this.selectedUserForManagement = user;
      this.userDepartments = user.departments || [];
      this.manageDepartmentsDialog = true;
    },

    closeMangeDepartmentsDialog() {
      this.manageDepartmentsDialog = false;
      this.selectedUserForManagement = null;
      this.userDepartments = [];
    },

    openAddToDepartmentFromManage() {
      // Store the user reference before closing the management dialog
      const userToAdd = this.selectedUserForManagement;
      this.closeMangeDepartmentsDialog();
      this.openAddToDepartmentDialog(userToAdd, true); // Pass true to indicate from management
    },

    // Edit Department methods
    openEditDepartmentDialog(userDept) {
      //console.log('Opening edit dialog for user dept:', userDept);
      this.editingDepartmentData = {
        id: userDept.id,
        department_id: userDept.department_id,
        department_job_id: userDept.department_job_id,
        active: userDept.active,
        roles_ids: userDept.roles?.map(role => role.id) || []
      };
      
      // Filter department jobs based on the department
      //console.log('Filtering jobs for department ID:', userDept.department_id);
      //console.log('All department jobs:', this.departmentJobs);
      this.filteredEditDepartmentJobs = this.departmentJobs.filter(
        job => job.department_id === userDept.department_id
      );
      //console.log('Filtered edit department jobs:', this.filteredEditDepartmentJobs);
      
      this.editDepartmentDialog = true;
    },

    closeEditDepartmentDialog() {
      this.editDepartmentDialog = false;
      this.editingDepartmentData = {
        id: null,
        department_id: null,
        department_job_id: null,
        active: true,
        roles_ids: []
      };
      this.filteredEditDepartmentJobs = [];
      this.$refs.editDepartmentForm?.reset();
    },

    async updateUserDepartment() {
      if (!this.$refs.editDepartmentForm.validate()) return;
      
      try {
        this.loading = true;
        
        const updateData = {
          department_job_id: this.editingDepartmentData.department_job_id,
          active: this.editingDepartmentData.active ? 1 : 0,
          roles_ids: this.editingDepartmentData.roles_ids
        };

        const response = await userService.updateUserDepartment(
          this.selectedUserForManagement.id,
          this.editingDepartmentData.id,
          updateData
        );
        
        showSuccessAlert(this.$store, response, 'تم تحديث بيانات القسم بنجاح');

        this.closeEditDepartmentDialog();
        await this.fetchUsers(); // Refresh users list
        
        // Refresh the manage dialog if still open
        if (this.manageDepartmentsDialog) {
          const updatedUser = this.users.find(u => u.id === this.selectedUserForManagement.id);
          if (updatedUser) {
            this.selectedUserForManagement = updatedUser;
            this.userDepartments = updatedUser.departments || [];
          }
        }
        
      } catch (error) {
        console.error('Error updating user department:', error);
        showErrorAlert(this.$store, error, 'فشل في تحديث بيانات القسم');
      } finally {
        this.loading = false;
      }
    },

    // Remove from Department methods
    confirmRemoveFromDepartment(userDept) {
      this.departmentToRemove = userDept;
      this.removeFromDepartmentDialog = true;
    },

    async executeRemoveFromDepartment() {
      try {
        this.loading = true;
        
        const response = await userService.removeUserFromDepartment(
          this.selectedUserForManagement.id,
          this.departmentToRemove.id
        );
        
        showSuccessAlert(this.$store, response, 
          `تم إزالة المستخدم من قسم "${this.getDepartmentName(this.departmentToRemove.department_id)}" بنجاح`
        );

        this.removeFromDepartmentDialog = false;
        this.departmentToRemove = null;
        
        await this.fetchUsers(); // Refresh users list
        
        // Refresh the manage dialog if still open
        if (this.manageDepartmentsDialog) {
          const updatedUser = this.users.find(u => u.id === this.selectedUserForManagement.id);
          if (updatedUser) {
            this.selectedUserForManagement = updatedUser;
            this.userDepartments = updatedUser.departments || [];
          }
        }
        
      } catch (error) {
        console.error('Error removing user from department:', error);
        showErrorAlert(this.$store, error, 'فشل في إزالة المستخدم من القسم');
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.users-container {
  padding: 16px;
}

:deep(.v-data-table) {
  background-color: white;
}

:deep(.v-data-table-header) {
  background-color: #f5f5f5;
}
</style> 